import React, { useEffect, useRef, useState } from "react";
import { Modal, Container } from "react-bootstrap";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import MasterViewController from "../../redux/actions/masterApiActions";
import HolidayViewController from "../../redux/actions/HolidayApiAction";

import { getUserInfoFromToken } from "../../utils/authUtils";

function HolidayMasterFormModal({
  show,
  onHide,
  selectedHolidays,
  setSelectedHolidays,
  initialData = null,
}) {
  const dispatch = useDispatch();
  const { addHolidays, updateHoliday } = MasterViewController();
  const { addEmployerHoliday } = HolidayViewController();
  const { holidays } = useSelector((state) => state.master);
  const { isLoading } = useSelector((state) => state.holiday || {});

  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    const { userType, userId } = getUserInfoFromToken();
    setUserType(userType);
    setUserId(userId);
  }, []);

  // Collect unique years from API data
  const availableYears = React.useMemo(() => {
    const yrs = Object.values(holidays)
      .flat()
      .map((h) => h.year)
      .filter(Boolean);
    const current = new Date().getFullYear();
    // Ensure current year is always present
    return Array.from(new Set([...yrs, current]))
      .sort((a, b) => b - a);
  }, [holidays]);

  // Filter holidays by selected year
  const filteredHolidays = React.useMemo(() => {
    return Object.values(holidays)
      .flat()
      .filter((h) => h.year === selectedYear);
  }, [holidays, selectedYear]);

  // --- validation / admin submit remains same ---
  const validationSchema = Yup.object({
    occasion: Yup.string().required("Occasion is required"),
    date: Yup.date().required("Date is required"),
    year: Yup.number().typeError("Year must be a number").required("Year is required"),
  });

  const initialValues = {
    occasion: initialData?.occasion || "",
    date: initialData?.date || "",
    year: initialData?.year || new Date().getFullYear(),
  };

  const handleAdminSubmit = (values, { resetForm }) => {
    const action = isEdit
      ? updateHoliday(initialData.id, values)
      : addHolidays(values);
    dispatch(action)
      .then(() => {
        resetForm();
        onHide();
      })
      .catch((err) => console.error("Error submitting holiday:", err));
  };

  const handleEmployerSubmit = () => {
    const allHolidays = Object.values(holidays).flat();
    const selected = allHolidays.filter((holiday) =>
      selectedHolidays.includes(holiday.id)
    );
    dispatch(addEmployerHoliday(selected))
      .then(() => {
        setSelectedHolidays([]);
        onHide();
      })
      .catch((err) => console.error("Error adding employer holidays:", err));
  };

  const handleSelect = (holidayId) => {
    setSelectedHolidays((prev) =>
      prev.includes(holidayId)
        ? prev.filter((id) => id !== holidayId)
        : [...prev, holidayId]
    );
  };

  return (
    <Dialog open={show} onClose={onHide} fullWidth maxWidth="md">
      {/* ---------- Title + Year dropdown for Employer ---------- */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        {userType === "Admin"
          ? isEdit
            ? "Edit Holiday"
            : "Add Holiday"
          : "Select Holidays"}

        {userType !== "Admin" && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogTitle>

      {userType === "Admin" ? (
        // ---------- Admin Form (unchanged) ----------
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleAdminSubmit}
          enableReinitialize
        >
          {({ values, handleChange, touched, errors }) => (
            <Form>
              <DialogContent dividers>
                <Stack spacing={3}>
                  <TextField
                    label="Occasion"
                    name="occasion"
                    value={values.occasion}
                    onChange={handleChange}
                    error={touched.occasion && Boolean(errors.occasion)}
                    helperText={touched.occasion && errors.occasion}
                    fullWidth
                    required
                  />
                  <TextField
                    type="date"
                    label="Date"
                    name="date"
                    InputLabelProps={{ shrink: true }}
                    value={values.date}
                    onChange={handleChange}
                    error={touched.date && Boolean(errors.date)}
                    helperText={touched.date && errors.date}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Year"
                    name="year"
                    value={values.year}
                    onChange={handleChange}
                    error={touched.year && Boolean(errors.year)}
                    helperText={touched.year && errors.year}
                    fullWidth
                    required
                  />
                </Stack>
              </DialogContent>

              <DialogActions>
                <Button onClick={onHide} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {isEdit ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      ) : (
        // ---------- Employer Table with Year filter ----------
        <>
          <DialogContent dividers>
            <Table
              size="small"
              sx={{
                border: "1px solid rgba(224,224,224,1)",
                "& th, & td": {
                  border: "1px solid rgba(224,224,224,1)",
                },
                "& thead th": {
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Occasion</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Day</TableCell>
                  <TableCell>Year</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHolidays.map((holiday) => (
                  <TableRow key={holiday.id} hover>
                    <TableCell>
                      <Checkbox
                        checked={selectedHolidays.includes(holiday.id)}
                        onChange={() => handleSelect(holiday.id)}
                      />
                    </TableCell>
                    <TableCell>{holiday.id}</TableCell>
                    <TableCell>{holiday.occasion}</TableCell>
                    <TableCell>{holiday.date}</TableCell>
                    <TableCell>{holiday.day}</TableCell>
                    <TableCell>{holiday.year}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>

          <DialogActions>
            <Button onClick={onHide} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleEmployerSubmit}
              variant="contained"
              color="primary"
            >
              Submit
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}


export default HolidayMasterFormModal;


